import { type HTMLProps, memo, useContext, useMemo } from 'react';
import ReactSlider from 'react-slider';

import styled from 'styled-components';

import styles from './Slider.module.css';
import { AllInputDataContext } from './../../../config';

const thumbStyle = () => `
  height: 77%;
  line-height: 22px;
  cursor:grab;
  align-items: center;
  display: flex;
  outline: none;
width:40px;
    justify-content: center;
`;

const StyledThumb = styled.div<{ color: string; name: string }>`
  ${() => thumbStyle()}
`;

type ThumbProps = {
  // react-slider's render prop supplies HTMLProps incl. a legacy `ref` and an
  // `as` attribute; omit both so the spread onto the styled component is clean.
  props: Omit<HTMLProps<HTMLDivElement>, 'ref' | 'as'>;
  state: { valueNow: number };
  color: string;
  name: string;
};

const Thumb = ({ props, state, color, name }: ThumbProps) => (
  <StyledThumb {...props} color={color} name={name}>
    <div
      className={styles.thumbMarkerBefore}
      style={{ backgroundColor: color }}
    ></div>
    <div
      className={styles.thumbMarker}
      style={{ backgroundColor: color }}
    ></div>

    <div
      style={
        name === 'Solar'
          ? { border: '1px solid black', color: 'black', background: color }
          : { border: '1px solid white', color: 'white', background: color }
      }
      className={styles.thumbText}
    >
      {state.valueNow}GW
    </div>
  </StyledThumb>
);

const trackBar = (color: string) => `
      z-index:0;
      background: linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 55%), ${color};
      height: 100%;
      margin-right: 20px;
      border-radius: 6px 2px 2px 6px;
      box-shadow: inset 0 0 0 1px rgba(255,255,255,0.12), 0 1px 3px rgba(0,0,0,0.25);
      `;

const StyledTrack = styled.div<{ color: string; index: number }>`
  ${(props) => (props.index === 1 ? '' : trackBar(props.color))}
`;

type TrackProps = {
  // See note on ThumbProps.props re: omitting the legacy `ref` and `as`.
  props: Omit<HTMLProps<HTMLDivElement>, 'ref' | 'as'>;
  state: { index: number };
  color: string;
};

const Track = ({ props, state, color }: TrackProps) => (
  <StyledTrack {...props} color={color} index={state.index} />
);

type Props = {
  value: number;
  originalValue: number;
  techIndexNumber: number;
  updateSliderValues: (value: number, techIndexNumber: number) => void;
};

const Slider = memo(
  ({ value, originalValue, techIndexNumber, updateSliderValues }: Props) => {
    const { technologyData } = useContext(AllInputDataContext);

    const initalSliderPosition = useMemo(
      () => (originalValue * 84) / technologyData[techIndexNumber].capMax + 8,
      [originalValue, techIndexNumber, technologyData]
    );

    return (
      <>
        <div className={styles.rangeSlider}>
          <div
            className={styles.thumbMarkerInital}
            style={{
              backgroundColor: technologyData[techIndexNumber].color,
              left: initalSliderPosition + '%',
            }}
          ></div>
          <ReactSlider
            value={value}
            onChange={(val) => updateSliderValues(val, techIndexNumber)}
            className={styles.horizontalSlider}
            step={0.1}
            renderThumb={(props, state) => (
              <Thumb
                props={props}
                state={state}
                color={technologyData[techIndexNumber].color}
                name={technologyData[techIndexNumber].name}
              />
            )}
            renderTrack={(props, state) => (
              <Track
                props={props}
                state={state}
                color={technologyData[techIndexNumber].color}
              />
            )}
            max={+technologyData[techIndexNumber].capMax}
          />
        </div>
      </>
    );
  }
);

export default Slider;
